import _ from 'lodash'
import React, { useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Cell, CellProps, Column, HeaderGroup, Row, useTable } from 'react-table'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import LoadingButton from '@mui/lab/LoadingButton'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import DelayedRender from '@/components/hoc/DelayedRender'
import Pagination from '@/components/ui/pagination/Pagination'
import TextField from '@/components/formik/fields/TextField'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import CheckboxField from '@/components/formik/fields/CheckboxField'

import { SearchInput } from './SearchInput'

type BaseOptionData = {
  id: string
}

type Field = {
  id: string
  align?: 'left' | 'center' | 'right'
  isMandatory: boolean
  name: string
  prefix?: string
  type: string
  placeholder?: string
  hint?: string
}

export type OptionsTableHandle = {
  toggleDeleteOptionDialog: () => void
  toggleOptionFormDialog: () => void
}

export type OptionsTableProps<D extends BaseOptionData> = {
  data: D[]
  fields: readonly Field[]
  isCreating?: boolean
  isDeleting?: boolean
  isListing?: boolean
  isUpdating?: boolean
  name: string
  onCreate?: (values: Partial<D>) => void
  onDelete?: (id: string) => void
  onSearch?: (text: string) => void
  onUpdate?: (id: string, values: Partial<D>) => void
  rowsPerPageOptions?: number[]
  toolbarActions?: React.ReactNode
}

export const OptionsTable = React.forwardRef(function OptionsTable<D extends BaseOptionData>(props: OptionsTableProps<D>, ref: React.Ref<OptionsTableHandle>) {
  const {
    data,
    fields,
    isCreating = false,
    isDeleting = false,
    isListing = false,
    isUpdating = false,
    name,
    onCreate,
    onDelete,
    onSearch,
    onUpdate,
    rowsPerPageOptions = [5, 10, 15],
    toolbarActions,
  } = props
  const { t } = useTranslation()

  useImperativeHandle(ref, () => ({
    toggleDeleteOptionDialog,
    toggleOptionFormDialog,
  }))

  const submitButtonContainerRef = useRef<HTMLDivElement>(null)

  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false)
  const [optionFormDialogOpen, setOptionFormDialogOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0])
  const [selectedOption, setSelectedOption] = useState<D>()

  const columns = useMemo(() => {
    return _.map(fields, (field) => ({
      Header: field.name,
      accessor: field.id,
      Cell: ({ value }: CellProps<D>) =>
        typeof value === 'boolean' ? (
          <span>{value ? <CheckCircleOutlineRoundedIcon color="success" /> : <HighlightOffRoundedIcon color="error" />}</span>
        ) : (
          value
        ),
      align: field.align || 'left',
      width: 'unset',
    })) as Column<D>[]
  }, [fields])

  const initialValues = useMemo(
    () =>
      _.reduce(
        fields,
        (values: Partial<D>, field) => {
          let defaultValue
          switch (field.type) {
            case 'number':
            case 'string':
              defaultValue = field.prefix ? `${field.prefix}.` : ''
              break
            case 'boolean':
              defaultValue = false
              break
          }
          _.set(values, field.id, _.get(selectedOption, field.id, defaultValue))
          return values
        },
        {}
      ),
    [fields, selectedOption]
  )

  const validationSchema = useMemo(
    () =>
      yup.object(
        _.reduce(
          fields,
          (objectShape: Record<string, yup.ObjectSchema<Record<string, yup.AnySchema>>>, field) => {
            let fieldSchema: yup.BooleanSchema | yup.NumberSchema | yup.StringSchema
            switch (field.type) {
              case 'boolean':
                fieldSchema = yup.boolean()
                break
              case 'number':
                fieldSchema = yup.number()
                break
              default:
                fieldSchema = yup.string()
                break
            }
            if (field.isMandatory) fieldSchema = fieldSchema.required()

            // if field id does not contain ".", schema is not an object schema
            if (field.id.indexOf('.') === -1) _.set(objectShape, field.id, fieldSchema)
            // else schema has the nested object schema
            else {
              const paths: string[] = field.id.split('.')
              const reversePaths: string[] = [...paths].reverse()
              const nestedSchema = yup.object(
                _.reduce(
                  reversePaths.slice(0, reversePaths.length - 1),
                  (yupObj: typeof objectShape, path: string) => {
                    _.set(yupObj, path, reversePaths[0] === path ? fieldSchema : yup.object(yupObj))
                    return yupObj
                  },
                  {}
                )
              )
              // TODO: fixed typing
              // @ts-ignore
              objectShape[paths[0]] = objectShape[paths[0]] ? objectShape[paths[0]].concat(nestedSchema) : nestedSchema
            }
            return objectShape
          },
          {}
        )
      ),
    [fields]
  )

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<D>({
    columns,
    data,
  })

  const emptyRows = Math.max(0, page * rowsPerPage - rows.length)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage)
    setPage(1)
  }

  const handleConfirmDelete = () => {
    if (selectedOption) onDelete?.(selectedOption.id)
  }

  const handleDelete = (row: Row<D>) => () => {
    setSelectedOption(row.original)
    setDeleteOptionDialogOpen(true)
  }

  const handleEdit = (row: Row<D>) => () => {
    setSelectedOption(row.original)
    setOptionFormDialogOpen(true)
  }

  const handleSubmit = (values: typeof initialValues) => {
    if (!selectedOption) {
      onCreate?.(values)
    } else {
      onUpdate?.(selectedOption.id, values)
    }
  }

  const toggleDeleteOptionDialog = () => {
    setDeleteOptionDialogOpen((prevOpen) => !prevOpen)
    setSelectedOption(undefined)
  }

  const toggleOptionFormDialog = () => {
    setOptionFormDialogOpen((prevOpen) => !prevOpen)
    setSelectedOption(undefined)
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', p: '10px 12px' }}>
        <SearchInput onSearch={onSearch} />
        <Stack direction="row" spacing={1}>
          {toolbarActions}
          <Button onClick={toggleOptionFormDialog} variant="contained">
            {t('page.superAdmin.settings.createOptionButton.label')}
          </Button>
        </Stack>
      </Stack>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            {headerGroups.map((headerGroup: HeaderGroup<D>) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: HeaderGroup<D>) => {
                  return (
                    <TableCell
                      {...column.getHeaderProps()}
                      align={column.align || 'left'}
                      scope="col"
                      sx={{ width: column.width, maxWidth: column.maxWidth, minWidth: column.minWidth, wordWrap: 'break-word' }}
                    >
                      {column.render('Header')}
                    </TableCell>
                  )
                })}
                <TableCell align="right">{t('common:table.header.action')}</TableCell>
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
            {isListing && (
              <DelayedRender delay={300}>
                <TableRow>
                  <TableCell sx={{ border: 'none', padding: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  </TableCell>
                </TableRow>
              </DelayedRender>
            )}
            {rows.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((row) => {
              prepareRow(row)
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell: Cell<D>) => (
                    <TableCell {...cell.getCellProps()} align={cell.column.align || 'left'}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Stack justifyContent="flex-end" direction="row" spacing={1}>
                      <Tooltip title={t('common:button.modify.tooltip')}>
                        <ChipButton color="info" onClick={handleEdit(row)}>
                          <EditRoundedIcon />
                        </ChipButton>
                      </Tooltip>
                      <Tooltip title={t('common:button.delete.tooltip')}>
                        <ChipButton color="error" onClick={handleDelete(row)}>
                          <DeleteRoundedIcon />
                        </ChipButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 63 * emptyRows }}>
                <TableCell colSpan={columns.length + 2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
          <Pagination
            color="primary"
            itemsPerPageOptions={rowsPerPageOptions}
            shape="rounded"
            page={page}
            onPageChange={handleChangePage}
            onItemsPerPageChange={handleChangeRowsPerPage}
            totalItems={rows.length}
          />
        </Box>
      </TableContainer>
      <Dialog
        open={optionFormDialogOpen}
        onClose={toggleOptionFormDialog}
        PaperProps={{ sx: { maxWidth: (theme) => theme.breakpoints.values.sm, width: '100%' } }}
      >
        <DialogTitle>{t('page.superAdmin.settings.optionFormDialog.title', { context: !selectedOption ? 'create' : 'update' })}</DialogTitle>
        <DialogContent sx={{ pb: '30px' }}>
          <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
            {(formikProps) => (
              <Form style={{ maxWidth: '400px', margin: '0 auto' }}>
                <Stack spacing={2}>
                  {_.map(fields, (field) => {
                    switch (field.type) {
                      case 'string':
                        return (
                          <Box key={field.id}>
                            <TextField
                              fullWidth
                              label={field.name}
                              name={field.id}
                              placeholder={field.placeholder}
                              {...(field.prefix && {
                                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                  const { value } = event.target
                                  if (!value || !_.startsWith(value, `${field.prefix}.`)) {
                                    formikProps.setFieldValue(field.id, `${field.prefix}.`)
                                  } else {
                                    formikProps.setFieldValue(field.id, value)
                                  }
                                },
                              })}
                              required={field.isMandatory}
                            />
                            {field.hint && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                {field.hint}
                              </Typography>
                            )}
                          </Box>
                        )
                      case 'boolean':
                        return <CheckboxField key={field.id} label={field.name} name={field.id} required={field.isMandatory} />
                      default:
                        return null
                    }
                  })}
                </Stack>
                <Portal container={submitButtonContainerRef.current}>
                  <LoadingButton disabled={!formikProps.isValid} loading={isCreating || isUpdating} onClick={formikProps.submitForm} variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Portal>
              </Form>
            )}
          </Formik>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleOptionFormDialog} variant="neutral">
            {t('common:button.cancel.label')}
          </Button>
          <Box ref={submitButtonContainerRef} ml={1} />
        </DialogActions>
      </Dialog>
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        onClose={toggleDeleteOptionDialog}
        onDelete={handleConfirmDelete}
        open={deleteOptionDialogOpen}
        title={t(`dialog.deleteConfirmation.${name}.title`)}
        message={t(`dialog.deleteConfirmation.${name}.message`)}
      />
    </Stack>
  )
})

export default OptionsTable
